import { format as dateFnsFormat, isValid } from 'date-fns';

/** User-facing date (tables, labels) — year, month name, day. */
export const APP_DATE_FORMAT = 'yyyy MMM dd';

/** User-facing date + time (pickers, tables). */
export const APP_DATETIME_FORMAT = 'yyyy MMM dd HH:mm';

/** API / MySQL datetime payload. */
export const APP_DATETIME_API_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** Jodit note body timestamp stamp (not list/display format). */
export const NOTE_EDITOR_TIMESTAMP_FORMAT = 'ddMMMyyyy HH:mm:ss';

/** MUI X pickers — keyboard input + calendar display. */
export const muiDateFormats = {
    year: 'yyyy',
    keyboardDate: APP_DATE_FORMAT,
    shortDate: APP_DATE_FORMAT,
    normalDate: APP_DATE_FORMAT,
    normalDateWithWeekday: 'EEE, yyyy MMM dd',
    fullDate: APP_DATE_FORMAT,
    keyboardDateTime: APP_DATETIME_FORMAT,
    fullDateTime: APP_DATETIME_FORMAT,
    keyboardDateTime12h: 'yyyy-MM-dd hh:mm a',
    fullTime: 'HH:mm:ss',
    hours24h: 'HH:mm',
} as const;

export type AppDateInput = Date | string | number | null | undefined;

export function toAppDate(value: AppDateInput): Date | null {
    if (value == null || value === '') {
        return null;
    }
    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }
    const normalized =
        typeof value === 'string' && value.includes(' ') && !value.includes('T')
            ? value.replace(' ', 'T')
            : value;
    const parsed = new Date(normalized);
    return isValid(parsed) ? parsed : null;
}

export function formatAppDate(value: AppDateInput): string {
    const date = toAppDate(value);
    return date ? dateFnsFormat(date, APP_DATE_FORMAT) : '';
}

export function formatAppDateTime(value: AppDateInput): string {
    const date = toAppDate(value);
    return date ? dateFnsFormat(date, APP_DATETIME_FORMAT) : '';
}

export function formatAppDateTimeApi(value: Date | null | undefined): string | null {
    if (!value) {
        return null;
    }
    const date = toAppDate(value);
    return date ? dateFnsFormat(date, APP_DATETIME_API_FORMAT) : null;
}

export function formatAppDateTimeOrDash(value: AppDateInput, placeholder = '—'): string {
    return formatAppDateTime(value) || placeholder;
}

export function formatNoteEditorTimestamp(date: Date = new Date()): string {
    return dateFnsFormat(date, NOTE_EDITOR_TIMESTAMP_FORMAT).toUpperCase();
}
